import { useEffect, useMemo, useState } from "react";
import { BrowserRouter, Routes, Route, Link, useLocation } from "react-router-dom";
import { Instagram, Linkedin } from "lucide-react";

import "@/App.css";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const nav = [
  ["Our Work", "/work"],
  ["Services", "/services"],
  ["Clients", "/clients"],
  ["About Us", "/about"],
  ["Careers", "/careers"],
];
const navSlug = (label) => label.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/(^-|-$)/g, "");
const footerNav = [...nav, ["Contact", "/contact"], ["Work with us", "/work-with-us"]];
const SOCIAL_LINKS = {
  instagram: "https://www.instagram.com/yoop.media/",
  linkedin: "https://www.linkedin.com/company/yoop-media/posts/?feedView=all",
};

function SiteFooter() {
  return (
    <footer className="site-footer" data-testid="site-footer">
      <div className="wrap footer2-inner">
        <div className="footer2-top">
          <Link to="/" aria-label="Yoop Media home" data-testid="footer-logo-link">
            <img src="/yoop-logo-full.png" alt="Yoop Media" className="footer2-logo"/>
          </Link>
          <div className="footer2-social">
            <a href={SOCIAL_LINKS.instagram} target="_blank" rel="noreferrer" aria-label="Instagram" data-testid="footer-instagram-link"><Instagram size={18}/></a>
            <a href={SOCIAL_LINKS.linkedin} target="_blank" rel="noreferrer" aria-label="LinkedIn" data-testid="footer-linkedin-link"><Linkedin size={18}/></a>
          </div>
        </div>
        <nav className="footer2-nav" aria-label="Footer" data-testid="footer-nav-links">
          {footerNav.map(([label, path]) => <Link key={path} to={path} data-testid={`footer-nav-${navSlug(label)}-link`}>{label}</Link>)}
        </nav>
        <div className="footer2-bottom">
          <span className="footer2-copyright" data-testid="footer-copyright">© 2026 Yoop Media. All rights reserved.</span>
          <span className="footer2-meta">
            <a href="mailto:admin@yoopmedia.com" data-testid="footer-email-link">admin@yoopmedia.com</a> · <a href="#" data-testid="footer-privacy-link">Privacy Policy</a> · <a href="#" data-testid="footer-license-link">License</a> · <a href="#" data-testid="footer-cookies-link">Cookies</a>
          </span>
        </div>
      </div>
    </footer>
  );
}
const stages = {
  brand: ["Brand", "Who are you, in one sentence a stranger would repeat? Positioning and identity enter the system once and set the terms for everything after."],
  creative: ["Creative", "What we create changes what people see. Ideas are built for the feed they will actually land in, not for the deck they were sold in."],
  distribution: ["Distribution", "The best idea with the wrong placement is a private joke. Format, platform and timing are creative decisions, not media admin."],
  performance: ["Performance", "What people see changes how they respond. Paid amplifies what already earns attention instead of buying its way past indifference."],
  data: ["Data", "What they do gives us the material to make the next idea better. This is where the loop closes and the compounding starts."],
};

function useMediaSlots() {
  const [slots, setSlots] = useState({});
  const refresh = async () => {
    try {
      const res = await fetch(`${API}/media`);
      if (!res.ok) return;
      const data = await res.json();
      const map = {};
      data.forEach(m => {
        if (m.role === "poster") {
          map[m.position] = { ...(map[m.position] || {}), poster: m.url };
        } else {
          map[m.position] = { ...(map[m.position] || {}), ...m };
        }
      });
      setSlots(map);
    } catch (_) {}
  };
  useEffect(() => { refresh(); }, []);
  return { slots, refresh };
}

const yoopLoopNodes = [
  { id: "diagnose", label: "Diagnose", text: "Market + Consumer + Compititor + Current Business" },
  { id: "define", label: "Define", text: "Positioning + Audience + Differentiation + Brand Strategy" },
  { id: "build", label: "Build", text: "Identify + Messaging + Content System" },
  { id: "distribute", label: "Distribute", text: "Organic + Creators + Paid + Partnerships" },
  { id: "convert", label: "Convert", text: "Landing page + website + whatsApp + Funnel + CRO" },
  { id: "retain", label: "Retain", text: "CRM + Community + Repeat Purchase + Loyalty" },
  { id: "measure", label: "Measure", text: "CAC + AOV + CVR + Retention + Contribution Margin" },
  { id: "scale", label: "Scale", text: "Double down on what the data validates" },
];
const yoopLoopCoords = yoopLoopNodes.map((_, i) => {
  const angle = ((-90 + i * 45) * Math.PI) / 180;
  return { x: 50 + 44 * Math.cos(angle), y: 50 + 40 * Math.sin(angle) };
});
const yoopLoopEdges = yoopLoopCoords.map((p, i) => {
  const n = yoopLoopCoords[(i + 1) % yoopLoopCoords.length];
  const mx = (p.x + n.x) / 2, my = (p.y + n.y) / 2;
  const cx = 50 + (mx - 50) * 1.16, cy = 50 + (my - 50) * 1.16;
  return { path: `M${p.x},${p.y} Q${cx},${cy} ${n.x},${n.y}` };
});

const clientLogos = [
  { name: "Viamigos", src: "/brands/viamigos.png" },
  { name: "Jovees Herbal", src: "/brands/jovees.png" },
  { name: "True Story", src: "/brands/truestory.png" },
  { name: "Singh Architects", src: "/brands/singh.png" },
  { name: "NuVie", src: "/brands/nuvie.png" },
  { name: "Tickled Pink", src: "/brands/tickledpink.png" },
  { name: "Muoro", src: "/brands/muoro.png" },
];

function Logo({ className = "" }) { return <img src="/yoop-logo-wordmark.png" alt="Yoop Media" className={`nav-mark-img ${className}`} data-testid="yoop-logo"/>; }
function Header({ open, setOpen }) {
  const { pathname } = useLocation();
  return <>
    <div className="nav" data-testid="main-navigation"><div className="pill">
      <Link to="/" aria-label="YOOP home" data-testid="nav-logo-link"><Logo /></Link>
      <div className="nav-links">{nav.map(([label, path]) => <Link key={path} to={path} className={pathname === path ? "active" : ""} data-testid={`nav-${navSlug(label)}-link`}>{label}</Link>)}</div>
      <div className="nav-actions"><Link className="nav-link-plain" to="/contact" data-testid="nav-contact-link">Contact</Link><Link className="nav-cta" to="/work-with-us" data-testid="nav-work-with-us-link">Work with us</Link><button className="burger" onClick={() => setOpen(true)} aria-label="Open menu" aria-expanded={open} data-testid="mobile-menu-open-button"><span/><span/></button></div>
    </div></div>
    <div className="drawer" data-open={open} aria-hidden={!open} data-testid="mobile-menu-drawer"><div className="drawer-top"><Logo/><button className="burger" onClick={() => setOpen(false)} aria-label="Close menu" data-testid="mobile-menu-close-button"><span className="cross-one"/><span className="cross-two"/></button></div><nav>{nav.map(([label, path]) => <Link key={path} to={path} onClick={() => setOpen(false)} data-testid={`drawer-${navSlug(label)}-link`}>{label}</Link>)}<Link to="/contact" onClick={() => setOpen(false)} data-testid="drawer-contact-link">Contact</Link><Link to="/work-with-us" onClick={() => setOpen(false)} data-testid="drawer-work-with-us-link">Work with us</Link></nav><p className="label mute">Brand. Creative. Growth.</p></div>
  </>;
}

const workCaseStudies = [
  {
    id: "viamigos",
    badge: "Viamigos",
    title: "Aapki Trip Aapke sath",
    desc: "We turned travel content into scroll-stopping entertainment, mixing relatable travel chaos, cheeky humour, creator-led storytelling, useful travel hacks, and irresistible destination inspiration. From roasting overpriced trips to making visas and flight deals feel fun, every piece was designed to feel less like an ad and more like something you'd actually send to your travel-obsessed friend.",
    image: "/work/viamigos-case.jpg",
  },
  {
    id: "fairdeal",
    badge: "Fairdeal Market",
    title: "The Ads Campaign",
    desc: "We Led full-funnel Meta Ads campaigns for Fairdeal Market, a B2B quick-commerce platform targeting Gurugram, driving customer acquisition, app installs, conversions, and revenue. Generated 2,100+ app installs and ₹1 Cr+ in revenue, while optimizing campaigns for efficiency and achieving an impressive 53x ROAS through performance-led targeting and continuous optimization.",
    image: "/work/fairdeal-case.jpg",
  },
];

function WorkPage() {
  const [open, setOpen] = useState(false);
  useEffect(() => { const onKey = e => e.key === "Escape" && setOpen(false); window.addEventListener("keydown", onKey); return () => window.removeEventListener("keydown", onKey); }, []);
  return (
    <div className="App">
      <a className="skip" href="#main" data-testid="skip-link">Skip to content</a>
      <Header open={open} setOpen={setOpen}/>
      <main id="main" className="work-page" data-testid="page-our-work">
        <div className="work-grain" aria-hidden="true"/>
        <section className="work-hero wrap">
          <h1 className="display work-title" data-testid="work-title">Our work</h1>
          <p className="work-sub" data-testid="work-subtitle">No safe bets. No copy-paste thinking.<br/>Just bold, unapologetic ideas born from late nights, wild briefs, and a little bit of beautiful chaos.</p>
        </section>
        <section className="work-cases wrap" data-testid="work-cases-section">
          {workCaseStudies.map(cs => (
            <article className="work-case" key={cs.id} data-testid={`work-case-${cs.id}`}>
              <div className="work-case-left">
                <span className="work-case-badge" data-testid={`work-case-badge-${cs.id}`}>{cs.badge}</span>
                <h3 className="work-case-title" data-testid={`work-case-title-${cs.id}`}>{cs.title}</h3>
                <p className="work-case-desc" data-testid={`work-case-desc-${cs.id}`}>{cs.desc}</p>
              </div>
              <div className="work-case-media">
                <img src={cs.image} alt={cs.title} loading="lazy" data-testid={`work-case-image-${cs.id}`}/>
              </div>
            </article>
          ))}
        </section>
      </main>
      <SiteFooter/>
    </div>
  );
}

const servicesMenu = [
  { id: "branding", title: "Yoop Branding", desc: "We create brand system that make you instantly recognizable.", tags: ["Brand Strategy", "Visual Identity", "Positioning", "Messaging", "Copywriting", "Creative Design"] },
  { id: "social", title: "Yoop Social Management", desc: "No generic strategy, we make the strategy, that is built around your brand.", tags: ["Content Planning", "Platform Management", "Posting", "Community Engagement", "Social Strategy"] },
  { id: "content", title: "Yoop Content", desc: "Content simply designed to earn attention. And be remembered.", tags: ["Strategy", "Creative Concepts", "Scripting", "Production", "Reels", "UGC", "Carousels", "Videography", "Photography", "Editing", "Post Production"] },
  { id: "performance", title: "Yoop Performance", desc: "Conversion Focused growth.", tags: ["Meta Ads", "Google Ads", "Campaign Strategy", "Creative Testing", "Audience Targeting", "Optimisation", "Conversion-Focused Growth"] },
];

function ServicesPage() {
  const [open, setOpen] = useState(false);
  const [openMenu, setOpenMenu] = useState(0);
  useEffect(() => { const onKey = e => e.key === "Escape" && setOpen(false); window.addEventListener("keydown", onKey); return () => window.removeEventListener("keydown", onKey); }, []);
  return (
    <div className="App">
      <a className="skip" href="#main" data-testid="skip-link">Skip to content</a>
      <Header open={open} setOpen={setOpen}/>
      <main id="main" className="services-page" data-testid="page-services">
        <div className="work-grain" aria-hidden="true"/>
        <section className="services-hero wrap">
          <div className="loop2-diagram" data-testid="yoop-loop-diagram">
            <svg className="loop2-arrows" viewBox="0 0 100 100" preserveAspectRatio="none" aria-hidden="true">
              {yoopLoopEdges.map((e, i) => <path key={i} className="loop2-arrow-path" d={e.path}/>)}
            </svg>
            <div className="loop2-title" data-testid="yoop-loop-title">
              <span>The Y<i>oo</i>p Loop</span>
            </div>
            {yoopLoopNodes.map((n, i) => (
              <div key={n.id} className="loop2-node" style={{ left: `${yoopLoopCoords[i].x}%`, top: `${yoopLoopCoords[i].y}%` }} data-testid={`loop2-node-${n.id}`}>
                <span className="loop2-node-label">{n.label}</span>
                <span className="loop2-node-text">{n.text}</span>
              </div>
            ))}
          </div>
        </section>
        <section className="services-menu wrap" data-testid="services-menu">
          {servicesMenu.map((item, i) => {
            const isOpen = openMenu === i;
            return (
              <div className="menu-row" key={item.id} data-open={isOpen} data-testid={`services-menu-row-${item.id}`}>
                <button type="button" className="menu-row-head" onClick={() => setOpenMenu(isOpen ? null : i)} aria-expanded={isOpen} data-testid={`services-menu-toggle-${item.id}`}>
                  <span className="menu-num">{String(i + 1).padStart(2, "0")}</span>
                  <span className="menu-title">{item.title}</span>
                  <span className={`menu-icon${isOpen ? " is-open" : ""}`} aria-hidden="true"/>
                </button>
                <div className="menu-body">
                  <div className="menu-body-inner">
                    <p className="menu-desc" data-testid={`services-menu-desc-${item.id}`}>{item.desc}</p>
                    <div className="menu-tags">
                      {item.tags.map((tag, ti) => <span className="menu-tag" key={ti} data-testid={`services-menu-tag-${item.id}-${ti}`}>{tag}</span>)}
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </section>
        <section className="services-pricing wrap" data-testid="services-pricing">
          <p className="pricing-text">Starting now at <span className="pricing-amount" data-testid="services-pricing-amount">₹ 55,000</span></p>
        </section>
        <BriefSection/>
      </main>
      <SiteFooter/>
    </div>
  );
}

const briefSteps = [
  { num: "01", text: "You fill in the brief" },
  { num: "02", text: "We review & schedule a call" },
  { num: "03", text: "We scope and kick things off" },
];

function BriefSection({ standalone }) {
  const [form, setForm] = useState({ name: "", email: "", company: "", message: "" });
  const [status, setStatus] = useState("idle");
  const onChange = (field) => (e) => setForm(f => ({ ...f, [field]: e.target.value }));
  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim()) return;
    setStatus("sending");
    try {
      const res = await fetch(`${API}/brief`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ ...form, source: standalone ? "work-with-us" : "services" }),
      });
      if (!res.ok) throw new Error("failed");
      setStatus("success");
      setForm({ name: "", email: "", company: "", message: "" });
    } catch (_) {
      setStatus("error");
    }
  };
  return (
    <section className="brief-section wrap" data-testid="brief-section">
      <div className="brief-grid">
        <div className="brief-left">
          <h2 className="brief-heading">
            <span className="brief-heading-line">Let's</span>
            <span className="brief-heading-line brief-heading-underline">Build your Brand Together</span>
          </h2>
          <p className="brief-para">We partner with brands that want to stand out in the Hustle. Drop us a note and we will be in touch within 24 hours.</p>
          <div className="brief-steps">
            {briefSteps.map(s => (
              <div className="brief-step" key={s.num}>
                <span className="brief-step-num">{s.num}</span>
                <span className="brief-step-text">{s.text}</span>
              </div>
            ))}
          </div>
        </div>
        <form className="brief-form" onSubmit={onSubmit} data-testid="brief-form">
          <label className="brief-field">
            <span className="brief-label">Full Name*</span>
            <input className="brief-input" required value={form.name} onChange={onChange("name")} data-testid="brief-input-name"/>
          </label>
          <label className="brief-field">
            <span className="brief-label">Work Email*</span>
            <input className="brief-input" type="email" required value={form.email} onChange={onChange("email")} data-testid="brief-input-email"/>
          </label>
          <label className="brief-field">
            <span className="brief-label">Company</span>
            <input className="brief-input" value={form.company} onChange={onChange("company")} data-testid="brief-input-company"/>
          </label>
          <label className="brief-field">
            <span className="brief-label">Tell us about your project</span>
            <textarea className="brief-textarea" rows={4} value={form.message} onChange={onChange("message")} data-testid="brief-input-message"/>
          </label>
          <button type="submit" className="brief-submit" disabled={status === "sending"} data-testid="brief-submit-button">
            {status === "sending" ? "Sending…" : "Submit brief"}
          </button>
          {status === "success" && <p className="brief-status brief-status-success" data-testid="brief-status-success">Thanks — we'll be in touch within 24 hours.</p>}
          {status === "error" && <p className="brief-status brief-status-error" data-testid="brief-status-error">Something went wrong, please try again.</p>}
        </form>
      </div>
    </section>
  );
}

function WorkWithUsPage() {
  const [open, setOpen] = useState(false);
  useEffect(() => { const onKey = e => e.key === "Escape" && setOpen(false); window.addEventListener("keydown", onKey); return () => window.removeEventListener("keydown", onKey); }, []);
  return (
    <div className="App">
      <a className="skip" href="#main" data-testid="skip-link">Skip to content</a>
      <Header open={open} setOpen={setOpen}/>
      <main id="main" className="work-with-us-page" data-testid="page-work-with-us">
        <div className="work-grain" aria-hidden="true"/>
        <BriefSection standalone/>
      </main>
      <SiteFooter/>
    </div>
  );
}

const careersDepartments = [
  { id: "design", title: "Design", roles: [
    { title: "Graphic Designer", meta: "Hybrid, Delhi NCR" },
    { title: "Graphic Designer Intern", meta: "Hybrid, Delhi NCR" },
  ] },
  { id: "copy", title: "Copy", roles: [
    { title: "Copywriter Fresher/Intern", meta: "Hybrid, Delhi NCR" },
  ] },
  { id: "edit", title: "Edit", roles: [
    { title: "Video Editor", meta: "WFH, Delhi NCR" },
  ] },
  { id: "marketing", title: "Marketing", roles: [
    { title: "Performance Marketing Specialist", meta: "Hybrid/WFH, Delhi NCR" },
    { title: "Marketing Intern", meta: "Hybrid/WFH, Delhi NCR" },
  ] },
];

function CareersPage() {
  const [open, setOpen] = useState(false);
  const [openDept, setOpenDept] = useState(0);
  useEffect(() => { const onKey = e => e.key === "Escape" && setOpen(false); window.addEventListener("keydown", onKey); return () => window.removeEventListener("keydown", onKey); }, []);
  return (
    <div className="App">
      <a className="skip" href="#main" data-testid="skip-link">Skip to content</a>
      <Header open={open} setOpen={setOpen}/>
      <main id="main" className="work-page" data-testid="page-careers">
        <div className="work-grain" aria-hidden="true"/>
        <section className="work-hero wrap">
          <h1 className="display careers-title" data-testid="careers-title">Join the Y<span className="careers-red">oo</span>p community</h1>
          <p className="work-sub" data-testid="careers-subtitle">If playing it safe makes you restless, you're already one of us.</p>
        </section>
        <section className="services-menu wrap" data-testid="careers-menu">
          {careersDepartments.map((dept, i) => {
            const isOpen = openDept === i;
            return (
              <div className="menu-row" key={dept.id} data-open={isOpen} data-testid={`careers-dept-row-${dept.id}`}>
                <button type="button" className="menu-row-head" onClick={() => setOpenDept(isOpen ? null : i)} aria-expanded={isOpen} data-testid={`careers-dept-toggle-${dept.id}`}>
                  <span className="menu-num">{String(i + 1).padStart(2, "0")}</span>
                  <span className="menu-title">{dept.title}</span>
                  <span className={`menu-icon${isOpen ? " is-open" : ""}`} aria-hidden="true"/>
                </button>
                <div className="menu-body">
                  <div className="menu-body-inner">
                    {dept.roles.map((role, ri) => (
                      <div className="career-role-row" key={ri} data-testid={`careers-role-${dept.id}-${ri}`}>
                        <div className="career-role-info">
                          <span className="career-role-title" data-testid={`careers-role-title-${dept.id}-${ri}`}>{role.title}</span>
                          <span className="career-role-meta" data-testid={`careers-role-meta-${dept.id}-${ri}`}>{role.meta}</span>
                        </div>
                        <Link to="/contact" className="contact-start-link" data-testid={`careers-role-apply-${dept.id}-${ri}`}>Apply <span aria-hidden="true">→</span></Link>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            );
          })}
        </section>
        <section className="careers-cta wrap" data-testid="careers-cta-section">
          <p className="careers-cta-text" data-testid="careers-cta-text">Send your Work Profile</p>
          <Link to="/contact" className="btn btn--fill" data-testid="careers-cta-apply-button">Apply</Link>
        </section>
      </main>
      <SiteFooter/>
    </div>
  );
}

function ContactPage() {
  const [open, setOpen] = useState(false);
  useEffect(() => { const onKey = e => e.key === "Escape" && setOpen(false); window.addEventListener("keydown", onKey); return () => window.removeEventListener("keydown", onKey); }, []);
  return (
    <div className="App">
      <a className="skip" href="#main" data-testid="skip-link">Skip to content</a>
      <Header open={open} setOpen={setOpen}/>
      <main id="main" className="work-page" data-testid="page-contact">
        <div className="work-grain" aria-hidden="true"/>
        <section className="work-hero wrap">
          <h1 className="display contact-title" data-testid="contact-hello-title">Say hello</h1>
        </section>
        <section className="contact-info-wrap wrap" data-testid="contact-info-section">
          <div className="contact-info-block">
            <div className="contact-field">
              <p className="brief-label" data-testid="contact-email-label">Email</p>
              <a href="mailto:admin@yoopmedia.com" className="contact-value" data-testid="contact-email-value">admin@yoopmedia.com</a>
            </div>
            <div className="contact-field">
              <p className="brief-label" data-testid="contact-phone-label">Phone</p>
              <a href="tel:+916392806209" className="contact-value" data-testid="contact-phone-value">+91 63928 06209</a>
            </div>
            <div className="contact-field">
              <p className="brief-label" data-testid="contact-address-label">Address</p>
              <p className="contact-value" data-testid="contact-address-value">Galaxy Blue Sapphire Mall, Greater Noida West,<br/>Greater Noida, Uttar Pradesh, India<br/>Pin code - 201318</p>
            </div>
            <hr className="rule contact-divider"/>
            <p className="brief-label contact-partner-text" data-testid="contact-partner-text">Looking to partner with us?</p>
            <Link to="/work-with-us" className="contact-start-link" data-testid="contact-start-project-link">Start a project <span aria-hidden="true">→</span></Link>
          </div>
        </section>
      </main>
      <SiteFooter/>
    </div>
  );
}

function EmptyPage({ title }) {
  const [open, setOpen] = useState(false);
  useEffect(() => { const onKey = e => e.key === "Escape" && setOpen(false); window.addEventListener("keydown", onKey); return () => window.removeEventListener("keydown", onKey); }, []);
  return (
    <div className="App">
      <a className="skip" href="#main" data-testid="skip-link">Skip to content</a>
      <Header open={open} setOpen={setOpen}/>
      <main id="main" className="empty-page" data-testid={`page-${navSlug(title)}`}></main>
      <SiteFooter/>
    </div>
  );
}

function MediaCell({ index, slot }) {
  const pos = index + 1;
  const src = slot?.url ? `${process.env.REACT_APP_BACKEND_URL}${slot.url}` : null;
  const poster = slot?.poster ? `${process.env.REACT_APP_BACKEND_URL}${slot.poster}` : undefined;
  return (
    <div className={`film-cell placeholder-${pos}`} data-testid={`media-placeholder-${pos}`}>
      {src ? (
        slot.kind === "video" ? (
          <video src={src} poster={poster} autoPlay muted loop playsInline data-testid={`media-video-${pos}`} />
        ) : (
          <img src={src} alt={`YOOP hero tile ${pos}`} data-testid={`media-image-${pos}`} />
        )
      ) : poster ? (
        <img src={poster} alt={`YOOP hero tile ${pos}`} data-testid={`media-image-${pos}`} />
      ) : (
        <span>MEDIA<br/>PLACEHOLDER</span>
      )}
    </div>
  );
}

function Hero({ slots }) {
  return <div className="stage" data-testid="hero-stage"><div className="stage-sticky"><div className="stage-mist"><i/><i/></div><div className="film-grid" data-testid="hero-media-grid">{Array.from({ length: 9 }).map((_, i) => <MediaCell key={i} index={i} slot={slots[i + 1]} />)}</div><div className="film-vignette"/><div className="hero"><div className="mark" data-testid="hero-wordmark"></div></div><span className="scrollcue">Scroll</span></div></div>;
}

function Noise() { const words = ["Ads.", "Reels.", "Offers.", "Notifications.", "Brands.", "Noise.", "Ads.", "Reels."]; return <section className="noise" data-testid="noise-section"><div className="noise-stage">{words.map((x, i) => <span className="noise-word" key={i} style={{ "--x": `${[14,78,30,82,20,66,52,90][i]}%`, "--y": `${[22,16,70,64,46,84,8,40][i]}%`, "--r": `${[-7,5,4,-6,-3,8,-4,9][i]}deg` }}>{x}</span>)}<h2 className="display noise-thesis" data-testid="noise-thesis"><span>Attention isn't the goal.</span><span>Impact is.</span></h2></div></section>; }
function Strips() { const a = ["Brand Strategy", "Positioning", "Creative Direction", "Short-form Content", "Founder-led Content", "Campaigns"]; const b = ["Performance Marketing", "Meta Ads", "Creative Testing", "Conversion Optimization", "Retargeting", "Analytics"]; return <>{[[a, "strip--red"], [b, "strip--line strip--rev"]].map(([items, cls], j) => <div className={`strip ${cls}`} key={j}><ul>{[...items, ...items].map((x, i) => <li key={i}>{x}</li>)}</ul></div>)}</>; }

function System() { const [active, setActive] = useState("creative"); const coords = { brand: [120, 210], creative: [500, 70], distribution: [640, 210], performance: [500, 350], data: [360, 210] }; return <section className="sect system" data-testid="system-section"><div className="wrap"><p className="label tint">The YOOP System</p><h2 className="display section-title">Creative and growth shouldn't compete. They should compound.</h2><div className="sys-grid"><div className="sys"><svg viewBox="0 0 760 420" role="img" aria-label="YOOP System diagram" data-testid="system-diagram"><circle cx="500" cy="210" r="140" fill="none" stroke="rgba(255,255,255,.18)"/><path d="M360 210 A140 140 0 0 1 500 70" fill="none" stroke="#FF2E1F" strokeWidth="2.5"/>{Object.entries(coords).map(([key, [cx, cy]]) => <g className={`node ${active === key ? "on" : ""}`} key={key} onClick={() => setActive(key)} onKeyDown={e => (e.key === "Enter" || e.key === " ") && setActive(key)} tabIndex="0" role="button" data-testid={`system-${key}-node`}><circle cx={cx} cy={cy} r="66"/><text x={cx} y={cy}>{key}</text></g>)}</svg></div><div><div className="sys-readout" aria-live="polite" data-testid="system-readout"><h3>{stages[active][0]}</h3><p>{stages[active][1]}</p></div><hr className="rule"/><p className="compound">We don't treat brand, creative and performance as separate worlds. <b>We connect them.</b> What performs informs what we create next.</p></div></div></div></section>; }

function Manifesto() { return <section className="sect manifesto" data-testid="manifesto-section"><div className="wrap">{["Less content. More consequence.", "Less noise. More signal.", 'Less "look at us." More "look at this."', "Less content for content's sake. More work that moves something."].map((x, i) => <p className="mani-line on" key={x}><small>0{i + 1}</small>{x}</p>)}</div></section>; }
function Work() { return <section className="sect wrap" id="work" data-testid="work-section"><p className="label tint">Work</p><h2 className="display section-title">Work that moved something.</h2><p className="lede mute">We don't build portfolios just to show what something looked like. We show what changed.</p>{[1, 2, 3].map(i => <article className="case" key={i}><div className="case-top"><p className="label mute">Case 0{i} — [Client name]</p><p className="label mute">[Category / Industry]</p></div><h3>[Project title]</h3><div className="spine">{["The problem", "The shift", "The idea", "The work", "The impact"].map(t => <div key={t}><h4>{t}</h4><p>{i === 1 ? "What was happening before YOOP got involved?" : " "}</p></div>)}</div><div className="metric"><strong>[  ]</strong><em>Primary result</em><span className="slot">Add real number</span></div></article>)}</section>; }
function Lab() { return <section className="sect lab" id="lab" data-testid="lab-section"><div className="wrap"><p className="label">YOOP Lab</p><h2 className="display section-title">Ideas we couldn't leave alone.</h2><p className="lede" style={{ opacity: .8 }}>Not everything we make gets published. Some ideas exist to test how far they can go.</p><div className="lab-index">{[["Hook #037", '"Your brand doesn\'t have a content problem."'], ["Observation #014", '"People don\'t hate ads. They hate being bored."'], ["Experiment #022", '"What happens when the product isn\'t shown until second 17?"']].map(([no, txt]) => <a className="lab-item" href="#lab" key={no} data-testid={`lab-${no.split(" ")[0].toLowerCase()}-link`}><span className="no">{no}</span><span className="txt">{txt}</span></a>)}</div><div className="lab-cats">{[["Hooks", "Ideas designed to stop the scroll."], ["Experiments", "Things we're testing because we don't know what will happen."], ["Observations", "Things we're noticing about people, brands and culture."], ["Concepts", "Ideas that deserve to exist, even if they don't belong to a client."]].map(([h, p]) => <div key={h}><h4>{h}</h4><p>{p}</p></div>)}</div></div></section>; }

function ClientStrip({ testid = "clients-section", withItemTestids = false, large = false }) {
  return (
    <section className={`clients-strip ${large ? "clients-strip--lg" : ""}`} data-testid={testid} aria-label="Brands we've worked with">
      <div className="clients-track">
        {[...Array(2)].map((_, r) => (
          <ul className="clients-row" key={r} aria-hidden={r === 1}>
            {clientLogos.map(({ name, src }) => (
              <li className="client-mark" key={`${r}-${name}`} data-testid={withItemTestids && r === 0 ? `client-${name.toLowerCase().replace(/[^a-z0-9]+/g,'-')}` : undefined}>
                <img src={src} alt={name} loading="lazy" draggable="false"/>
              </li>
            ))}
          </ul>
        ))}
      </div>
    </section>
  );
}

function ClientsPage() {
  const [open, setOpen] = useState(false);
  useEffect(() => { const onKey = e => e.key === "Escape" && setOpen(false); window.addEventListener("keydown", onKey); return () => window.removeEventListener("keydown", onKey); }, []);
  const topRow = clientLogos.slice(0, 3);
  const bottomRow = clientLogos.slice(3, 7);
  return (
    <div className="App">
      <a className="skip" href="#main" data-testid="skip-link">Skip to content</a>
      <Header open={open} setOpen={setOpen}/>
      <main id="main" className="clients-page" data-testid="page-clients">
        <div className="work-grain" aria-hidden="true"/>
        <section className="work-hero wrap">
          <h1 className="display clients-title" data-testid="clients-title">Brands that refused to <span className="clients-red">BLEND IN</span></h1>
          <p className="work-sub" data-testid="clients-subtitle">They came with a brief. We came back with ideas. A few brands we've helped turn attention into impact.</p>
        </section>
        <section className="clients-grid-wrap wrap">
          <div className="clients-grid-row" data-testid="clients-grid-top">
            {topRow.map(({ name, src }) => (
              <div className="client-box" key={name} data-testid={`client-box-${navSlug(name)}`}>
                <img src={src} alt={name} loading="lazy" draggable="false"/>
              </div>
            ))}
          </div>
          <div className="clients-grid-row" data-testid="clients-grid-bottom">
            {bottomRow.map(({ name, src }) => (
              <div className="client-box" key={name} data-testid={`client-box-${navSlug(name)}`}>
                <img src={src} alt={name} loading="lazy" draggable="false"/>
              </div>
            ))}
          </div>
        </section>
      </main>
      <SiteFooter/>
    </div>
  );
}

function About() { return <><section className="sect wrap" id="about" data-testid="about-section"><div className="two-col"><h2 className="display about-big">We like making things matter.</h2><div><p>Brands are producing more content than ever. Consumers are becoming harder to impress. YOOP exists somewhere between those two realities.</p><p className="mute">We combine brand thinking, internet-native creativity and performance intelligence to help ambitious brands become more distinctive, more relevant and more effective.</p><p className="mute">We don't believe brands need more content. They need better reasons to care.</p></div></div></section><section className="sect wrap" data-testid="founder-section"><hr className="rule"/><div className="founder"><div><p className="label tint">Founder</p><h2 className="display founder-name">Nandini Singh</h2><p className="label mute">Founder &amp; Creative Director</p></div><div><blockquote className="quote">"Distribution taught me what strategy decks never could: attention is earned in the first two seconds, or not at all."</blockquote><p>Nandini founded YOOP after building an audience the same way she now builds them for brands — from zero, with no paid spend. She writes, directs and performs internet-native short-form content, and has worked on more than 50 brand collaborations across fashion, consumer tech and services.</p><p className="mute">YOOP is the version of that practice built for other people's brands: brand thinking, creative that's made for the feed, and performance work that proves what it did.</p><ul className="stats">{[["190K+", "Followers"], ["150M+", "Organic views"], ["₹0", "Paid spend"], ["50+", "Brand collaborations"]].map(([a, b]) => <li key={b}><b>{a}</b><span>{b}</span></li>)}</ul></div></div></section><ClientStrip testid="clients-section" withItemTestids/></>; }

function Contact() { const [msg, setMsg] = useState(""); const submit = e => { e.preventDefault(); const f = new FormData(e.currentTarget); if (!f.get("name") || !String(f.get("email")).includes("@")) setMsg("Add a name and a working email so we can reply."); else setMsg("This form is not connected to an inbox yet. Add your endpoint to the form action before launch."); }; return <><section className="sect finale" data-testid="final-cta-section"><div className="wrap"><h2 className="drift">Got a brand</h2><p className="drift red-line">to move?</p><p>Tell us what you're building, what's not working, or where you want to go next.</p><a href="#contact" className="btn btn--fill" data-testid="start-project-button"><span>Start a project →</span></a></div></section><section className="sect wrap" id="contact" data-testid="contact-section"><p className="label tint">Contact</p><h2 className="display section-title">Tell us what's moving.</h2><form className="form" onSubmit={submit} data-testid="contact-form"><Field id="name" label="Name" required/><Field id="brand" label="Brand / Company"/><Field id="site" label="Website" placeholder="https://"/><Field id="email" label="Email" required type="email"/><div className="field field--wide"><label htmlFor="solve">What are you trying to solve?</label><textarea id="solve" name="problem" rows="2" data-testid="contact-problem-input"/></div><div className="field"><label htmlFor="need">What do you need help with?</label><select id="need" name="need" data-testid="contact-need-select">{["Brand", "Creative", "Growth", "All three", "Not sure yet"].map(x => <option key={x}>{x}</option>)}</select></div><Field id="budget" label="Approximate budget" placeholder="₹"/><Field id="timeline" label="Timeline" placeholder="When do you need this live?" wide/><div className="field--wide"><button className="btn btn--fill" type="submit" data-testid="contact-submit-button">Send it →</button></div><p className="formnote" role="status" data-testid="contact-form-message">{msg}</p></form></section></>; }
function Field({ id, label, type = "text", placeholder, required, wide }) { return <div className={`field ${wide ? "field--wide" : ""}`}><label htmlFor={id}>{label}</label><input id={id} name={id} type={type} placeholder={placeholder} data-testid={`contact-${id}-input`}/></div>; }
function Home() {
  const [open, setOpen] = useState(false);
  const { slots } = useMediaSlots();
  useEffect(() => { const onKey = e => e.key === "Escape" && setOpen(false); window.addEventListener("keydown", onKey); return () => window.removeEventListener("keydown", onKey); }, []);
  return (
    <div className="App">
      <a className="skip" href="#main" data-testid="skip-link">Skip to content</a>
      <Header open={open} setOpen={setOpen}/>
      <main id="main">
        <Hero slots={slots}/>
        <div className="after">
          <ClientStrip testid="clients-section-top" large/>
          <section className="sect wrap" data-testid="attention-copy-section"><p className="label attention-caption">A creative agency made by Creators</p><h2 className="attention-thesis" data-testid="attention-thesis"><span className="attention-line" data-testid="attention-line-1">The problem isn't that brands</span><span className="attention-line" data-testid="attention-line-2">aren't creating enough.</span><span className="attention-line" data-testid="attention-line-3">The problem is that most of it</span><span className="attention-line" data-testid="attention-line-4">gives people no reason to care.</span></h2></section>
          <Strips/>
        </div>
      </main>
      <SiteFooter/>
    </div>
  );
}

function Admin() {
  const [token, setToken] = useState(() => localStorage.getItem("yoop_admin_token") || "");
  const { slots, refresh } = useMediaSlots();
  const [busy, setBusy] = useState(null);
  const [note, setNote] = useState("");

  useEffect(() => { if (token) localStorage.setItem("yoop_admin_token", token); }, [token]);

  const upload = async (position, file) => {
    if (!file) return;
    setBusy(position); setNote("");
    const fd = new FormData();
    fd.append("position", String(position));
    fd.append("file", file);
    try {
      const res = await fetch(`${API}/media/upload`, { method: "POST", body: fd, headers: { "X-Admin-Token": token } });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || "Upload failed");
      setNote(`Slot ${position} updated.`);
      await refresh();
    } catch (e) {
      setNote(`Slot ${position}: ${e.message}`);
    } finally { setBusy(null); }
  };

  const clear = async (position) => {
    setBusy(position); setNote("");
    try {
      const res = await fetch(`${API}/media/${position}`, { method: "DELETE", headers: { "X-Admin-Token": token } });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || "Delete failed");
      setNote(`Slot ${position} cleared.`);
      await refresh();
    } catch (e) {
      setNote(`Slot ${position}: ${e.message}`);
    } finally { setBusy(null); }
  };

  return (
    <div className="admin" data-testid="admin-page">
      <header className="admin-head">
        <Link to="/" className="nav-mark" data-testid="admin-home-link">Y<i>O</i><i>O</i>P</Link>
        <div>
          <p className="label tint">Admin</p>
          <h1 className="display admin-title">Hero grid manager</h1>
        </div>
      </header>

      <section className="admin-token">
        <label className="label" htmlFor="admin-token">Admin token</label>
        <input
          id="admin-token"
          type="password"
          value={token}
          onChange={e => setToken(e.target.value)}
          placeholder="Enter ADMIN_TOKEN"
          data-testid="admin-token-input"
        />
        <p className="mute" style={{ fontSize: "0.85rem" }}>Stored locally in your browser. Uploads use this as the <code>X-Admin-Token</code> header.</p>
      </section>

      <section className="admin-grid" data-testid="admin-grid">
        {Array.from({ length: 9 }).map((_, i) => {
          const pos = i + 1;
          const slot = slots[pos];
          const src = slot ? `${process.env.REACT_APP_BACKEND_URL}${slot.url}` : null;
          return (
            <div className="admin-cell" key={pos} data-testid={`admin-cell-${pos}`}>
              <div className="admin-cell-preview">
                {slot ? (
                  slot.kind === "video" ? <video src={src} muted loop autoPlay playsInline /> : <img src={src} alt={`Slot ${pos}`} />
                ) : (
                  <span>Empty slot {pos}</span>
                )}
              </div>
              <div className="admin-cell-meta">
                <p className="label"><span className="tint">Slot {pos}</span> {slot ? `— ${slot.kind}` : ""}</p>
                <p className="mute" style={{ fontSize: "0.8rem", wordBreak: "break-all" }}>{slot?.original_filename || "—"}</p>
                <div className="admin-cell-actions">
                  <label className="btn btn--fill admin-upload-btn" data-testid={`admin-upload-btn-${pos}`}>
                    {busy === pos ? "Uploading…" : "Upload"}
                    <input
                      type="file"
                      accept="image/png,image/jpeg,image/webp,image/gif,video/mp4,video/webm,video/quicktime"
                      onChange={e => upload(pos, e.target.files?.[0])}
                      style={{ display: "none" }}
                      data-testid={`admin-file-input-${pos}`}
                    />
                  </label>
                  {slot && (
                    <button className="btn admin-clear-btn" onClick={() => clear(pos)} data-testid={`admin-clear-btn-${pos}`}>Clear</button>
                  )}
                </div>
              </div>
            </div>
          );
        })}
      </section>
      {note && <p className="formnote" data-testid="admin-note">{note}</p>}
    </div>
  );
}

export default function App() {
  return (
    <BrowserRouter>
      <Routes>
        <Route path="/" element={<Home/>} />
        <Route path="/admin" element={<Admin/>} />
        <Route path="/work" element={<WorkPage/>} />
        <Route path="/services" element={<ServicesPage/>} />
        <Route path="/clients" element={<ClientsPage/>} />
        <Route path="/about" element={<EmptyPage title="About Us"/>} />
        <Route path="/careers" element={<CareersPage/>} />
        <Route path="/contact" element={<ContactPage/>} />
        <Route path="/work-with-us" element={<WorkWithUsPage/>} />
      </Routes>
    </BrowserRouter>
  );
}
